import React, { useState, useEffect } from 'react';
import { useTransition } from '@react-spring/web';
import { animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import { isEditorActive, Placeholder, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import HomepageHeroCarouselComponent from './HomepageHeroCarousel.styles';
import { PlaceholderWrapper } from './HomepageHeroCarousel.styles';

import HomepageHeroCarouselSlide from '../HomepageHeroCarouselSlide/HomepageHeroCarouselSlide.jsx';

const options = {
  transform: {
    from: { transform: 'translateX(100%)', position: 'absolute' },
    enter: { transform: 'translateX(0)' },
    leave: { transform: 'translateX(-100%)' }
  },
};


const HomepageHeroCarousel = ({ ...props }) => {
  const {
    fields = {},
    params = {}, // Default to an empty object if params is undefined
    sitecoreContext,
    rendering = {},
  } = props;

  const [loading, setLoading] = useState(true);
  const [loadingMinHeight, setLoadingMinHeight] = useState('60vw');

  const { autoplay = '0' } = params; // Safely destructure autoplay
  const { site } = sitecoreContext;
  const experienceEditor = isEditorActive();
  const carouselItems = rendering?.placeholders?.['homepage-hero-carousel-content'];

  const slides = Array.isArray(carouselItems)
    ? carouselItems.map((item, index) => ({ ...item, slideIndex: index, key: index }))
    : [];
  const [currentSlide, setCurrentSlide] = useState(1);
// State to handle pause

  const totalSlides = slides.length;
  const transitions = useTransition(currentSlide, {
    from: { transform: 'translateX(100%)', opacity: 0 },
    enter: { transform: 'translateX(0%)', opacity: 1 },
    leave: { transform: 'translateX(-100%)', opacity: 0 },
    keys: currentSlide, // Use stable keys for each slide
    config: { tension: 300, friction: 30 },
  });


  const [pause, setPause] = useState(false);
  const [minHeight, setMinHeight] = useState('auto');

  const bind = useDrag(({ down, movement: [mx], direction: [xDir], cancel }) => {
    if (typeof window !== 'undefined') {
      const swipeThreshold = window.innerWidth / 2;
      if (down && Math.abs(mx) > swipeThreshold) {
        const newSlide = currentSlide + (xDir > 0 ? -1 : 1);
        cancel(); // This might be needed to stop the gesture handling momentarily
        setCurrentSlide(newSlide <= 0 ? totalSlides - 1 : newSlide % totalSlides);
        setPause(true);
      }
    }
  });

  useEffect(() => {

    if(slides.length > 0) {
      setLoading(false);
    }

    if (autoplay === '1' && !experienceEditor) {
      const timer = !pause
        ? setTimeout(() => {
            setCurrentSlide((currentSlide) => {
              return currentSlide === totalSlides ? 1 : currentSlide + 1;
            });
          }, 7000)
        : null;

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [currentSlide, pause, autoplay, totalSlides, experienceEditor, slides.length]);

  useEffect(() => {
    function updateMinHeight() {
      if (typeof window !== 'undefined') {
        // Match the aspect ratio of HomepageHeroCarouselComponent (e.g., 2:1)
        if (window.innerWidth < 1200) {
          setLoadingMinHeight(`${window.innerWidth / 2 + 200}px`);
        } else {
          setLoadingMinHeight(`${window.innerWidth / 2}px`);
        }
      }
    }
    updateMinHeight();
    window.addEventListener('resize', updateMinHeight);
    window.addEventListener('load', updateMinHeight);
    return () => window.removeEventListener('resize', updateMinHeight);
  }, []);


  if (experienceEditor || props?.sitecoreContext?.pageEditing) {
    return (
      <div className="home-hero-carousel">
        <div>
          <h1>Start Hero Home Carousel</h1>
        </div>
        <PlaceholderWrapper>
          <Placeholder name="homepage-hero-carousel-content" rendering={rendering} />
        </PlaceholderWrapper>
        <div>
          <h1>End Hero Home Carousel</h1>
        </div>
      </div>
    );
  }

  if (loading && site?.name !== 'bushs-beans-foodservices') {
    return (
        <div className="home-hero-carousel-loading" style={{backgroundColor: 'rgb(255, 202, 46)', minHeight: loadingMinHeight}}>
          <div className="tagline" style={{padding: '20px 1.25rem 15px'}}>
            <h1 className="tagline-h1" style={{color: '#d43631', fontSize: '7.5vw'}}>That <span style={{textDecoration: 'underline'}}>Beautiful</span> Bean Co.</h1>
          </div>
          <div className="placeholder-image-wrapper" style={{width: '100%', aspectRatio: '2/1'}}></div>
        </div>
    );
  }

  return (
    <HomepageHeroCarouselComponent minHeight={minHeight} site={site?.name} className={site?.name}>
      {currentSlide === 1 ? (
        <div style={{ position: 'absolute', width: '100%' }}>
          <HomepageHeroCarouselSlide
            rendering={slides[0]}
            setPause={setPause}
            key={slides[0]?.key}
            bind={bind}
            setCurrentSlide={setCurrentSlide}
            currentSlide={currentSlide}
            totalSlides={totalSlides}
            setMinHeight={setMinHeight}
            site={site?.name}
            {...fields}
          />
        </div>
      ) : (
        transitions((style, index) => {
          const slide = slides[index % totalSlides];
          return (
            <animated.div style={{ ...style, position: 'absolute', width: '100%' }} key={slide.key}>
              <HomepageHeroCarouselSlide
                rendering={slide}
                setPause={setPause}
                key={slide.key}
                bind={bind}
                setCurrentSlide={setCurrentSlide}
                currentSlide={currentSlide}
                totalSlides={totalSlides}
                setMinHeight={setMinHeight}
                site={site?.name}
                {...fields}
              />
            </animated.div>
          );
        })
      )}
    </HomepageHeroCarouselComponent>
  );
};

export default withSitecoreContext()(HomepageHeroCarousel);